import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, Table, TableHead, TableBody, TableRow, TableCell, Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { STableLoader } from '../../../shared_elements';
import { globalGetService } from '../../../utils/globalApiServices';
import { checkApiStatus, formatterCurrency } from '../../../utils';
import { isValidAmount } from '../../../utils/helper';

const getPeriodSubtitle = (start_date, end_date, mode) => {
    if (!start_date || !end_date) return '';
    const start = moment(start_date);
    const m = String(mode || 'monthly').toLowerCase();
    const year = start.format('YYYY');
    const q = start.quarter();

    if (m === 'monthly') {
        return start.format('MMM YYYY');
    }
    if (m === 'quarterly') {
        return `Q${q} ${year}`;
    }
    if (m === 'yearly') {
        return `Year ${year}`;
    }
    return start.format('MMM YYYY');
};

const CashflowDetailsModal = ({
    open,
    onClose,
    leaseSlug,
    currency,
    start_date,
    end_date,
    mode,
    handleItemClicked,
    filter
}) => {
    const [isLoading, setLoading] = useState(false);
    const [cashflowDetailsData, setCashflowDetailsData] = useState([]);

    useEffect(() => {
        if (!open || !start_date || !end_date || !currency) return;

        const apiMode = String(mode || 'monthly').toLowerCase();

        setLoading(true);
        setCashflowDetailsData([]);
        let url = `/cashflows/closing-balance?mode=${encodeURIComponent(apiMode)}&currency=${encodeURIComponent(currency)}&start_date=${encodeURIComponent(start_date)}&end_date=${encodeURIComponent(end_date)}`;
        if (leaseSlug) {
            url += `&lease_slug=${encodeURIComponent(leaseSlug)}`;
        }
        if (filter?.lease_status) {
            url += `&lease_status=${encodeURIComponent(filter.lease_status)}`;
        }
        if (filter?.asset) {
            const selectedAssets = Array.isArray(filter.asset) ? filter.asset : filter.asset.toString().split(',').filter(Boolean);
            const assetParam = selectedAssets.map((key) => {
                const [id, assetType] = key.toString().split('_');
                return assetType ? `${id},${assetType}` : key;
            }).join(',');
            url += `&asset=${encodeURIComponent(assetParam)}`;
        }
        if (filter?.lessor_ids) {
            const lessorIds = Array.isArray(filter.lessor_ids) ? filter.lessor_ids.join(',') : filter.lessor_ids;
            url += `&lessor_ids=${encodeURIComponent(lessorIds)}`;
        }
        if (filter?.lessee_ids) {
            const lesseeIds = Array.isArray(filter.lessee_ids) ? filter.lessee_ids.join(',') : filter.lessee_ids;
            url += `&lessee_ids=${encodeURIComponent(lesseeIds)}`;
        }

        globalGetService(url)
            .then(response => {
                if (checkApiStatus(response)) {
                    setCashflowDetailsData(response?.data?.data || []);
                }
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }, [open, start_date, end_date, leaseSlug, currency, mode, filter]);

    const detailRows = (cashflowDetailsData || []).flatMap((item) => item.details || []);

    const titleId = 'cashflow-details-dialog-title';
    const periodSubtitle = start_date && end_date ? getPeriodSubtitle(start_date, end_date, mode) : '';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby={titleId}>
            <DialogTitle id={titleId} className='dialog-header' sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography component="span" sx={{ fontSize: '0.95rem', fontWeight: 500, color: '#000' }}>
                    Closing Balance
                </Typography>
                <CloseIcon onClick={() => { onClose(); handleItemClicked('Close Icon') }} sx={{ cursor: 'pointer' }} />
            </DialogTitle>
            <DialogContent dividers sx={{ padding: '16px' }}>
                {periodSubtitle ? (
                    <Typography sx={{ mb: 1.5, fontSize: '0.875rem', fontWeight: 500, color: '#000' }}>
                        {periodSubtitle}
                    </Typography>
                ) : null}
                {isLoading ? (
                    <Box sx={{ p: 2 }}><STableLoader count={4} rowCount={5} /></Box>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <Table className='mui-table-format' stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ minWidth: '140px', fontWeight: 'bold' }}>Major Assembly</TableCell>
                                    <TableCell sx={{ minWidth: '140px', fontWeight: 'bold' }}>Event</TableCell>
                                    <TableCell sx={{ minWidth: '140px', fontWeight: 'bold' }}>Fund</TableCell>
                                    <TableCell sx={{ minWidth: '140px', fontWeight: 'bold' }}>Fund Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(detailRows || []).map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{row.major_assembly || '--'}</TableCell>
                                        <TableCell>{row.event || '--'}</TableCell>
                                        <TableCell>
                                            {isValidAmount(row.fund) ? `${formatterCurrency(row.currency).format(Number(row.fund))}` : '--'}
                                        </TableCell>
                                        <TableCell>
                                            {row.fund_date ? moment(row.fund_date).format('MMM DD, YYYY') : '--'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {(!detailRows || detailRows.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">No data available</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default CashflowDetailsModal;
